import React, { useState, useEffect } from 'react';
import './PaymentModal.css';

const PaymentModal = ({ isOpen, onClose, onSave, invoice = null }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    method: 'Cash',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  const paymentMethods = [
    'Cash',
    'Credit Card',
    'Bank Transfer',
    'Check',
    'PayPal',
    'Other'
  ];

  useEffect(() => {
    if (invoice) {
      const balance = invoice.total - (invoice.paidAmount || 0);
      setFormData(prev => ({
        ...prev,
        amount: balance,
        maxAmount: balance
      }));
    } else {
      setFormData({
        date: new Date().toISOString().split('T')[0],
        amount: 0,
        method: 'Cash',
        notes: '',
        maxAmount: 0
      });
    }
    setErrors({});
  }, [invoice, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    
    if (name === 'amount') {
      newValue = parseFloat(value) || 0;
      const balance = invoice ? invoice.total - (invoice.paidAmount || 0) : 0;
      if (invoice && newValue > balance) {
        newValue = balance;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.date) {
      newErrors.date = 'Payment date is required';
    }

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'Payment amount must be greater than 0';
    }

    if (invoice && formData.amount > invoice.total - (invoice.paidAmount || 0)) {
      newErrors.amount = `Payment amount cannot exceed balance of ${formatCurrency(invoice.total - (invoice.paidAmount || 0))}`;
    }

    if (!formData.method) {
      newErrors.method = 'Payment method is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(value || 0);
  };

  if (!isOpen || !invoice) return null;

  const balance = invoice.total - (invoice.paidAmount || 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content payment-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Payment</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="invoice-summary">
          <div className="summary-item">
            <span>Invoice:</span>
            <strong>{invoice.invoiceNumber}</strong>
          </div>
          <div className="summary-item">
            <span>Customer:</span>
            <strong>{invoice.billTo?.name || 'N/A'}</strong>
          </div>
          <div className="summary-item">
            <span>Total Amount:</span>
            <strong>{formatCurrency(invoice.total)}</strong>
          </div>
          <div className="summary-item">
            <span>Paid:</span>
            <strong>{formatCurrency(invoice.paidAmount)}</strong>
          </div>
          <div className="summary-item balance-item">
            <span>Balance:</span>
            <strong>{formatCurrency(invoice.total - (invoice.paidAmount || 0))}</strong>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="date">Payment Date *</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              max={new Date().toISOString().split('T')[0]}
              className={errors.date ? 'error' : ''}
            />
            {errors.date && <span className="error-message">{errors.date}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="amount">Payment Amount *</label>
            <div className="amount-input-group">
             
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                min="0.01"
                max={balance}
                step="0.01"
                placeholder="0.00"
                className={errors.amount ? 'error' : ''}
              />
              <span className="amount-overlay-text">
                Enter amount up to {formatCurrency(balance)}
              </span>
            </div>
            <small className="field-hint">
              Maximum: {formatCurrency(balance)}
            </small>
            {errors.amount && <span className="error-message">{errors.amount}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="method">Payment Method *</label>
            <select
              id="method"
              name="method"
              value={formData.method}
              onChange={handleChange}
              className={errors.method ? 'error form-select' : 'form-select'}
            >
              {paymentMethods.map(method => (
                <option key={method} value={method}>{method}</option>
              ))}
            </select>
            {errors.method && <span className="error-message">{errors.method}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes (Optional)</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Payment reference, transaction ID, etc."
              rows="3"
            />
          </div>

          <div className="payment-preview">
            <div className="preview-row">
              <span>Current Balance:</span>
              <strong>{formatCurrency(balance)}</strong>
            </div>
            <div className="preview-row">
              <span>Payment Amount:</span>
              <strong>-{formatCurrency(formData.amount)}</strong>
            </div>
            <div className="preview-row new-balance">
              <span>New Balance:</span>
              <strong>{formatCurrency(balance - (formData.amount || 0))}</strong>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save">
              Record Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;

